import * as moment from 'moment'
import { createSelector } from 'reselect'
import { formValueSelector, focus, change } from 'redux-form'
import { Guards } from 'interfaces/guards'
import { connectLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { homesGuard, carsGuard } from 'guards/propertyGuards'
import { FORM_ONBOARDING_PROPERTY } from 'constants/forms'
import { DATE_FORMAT_US } from 'constants/datetime'
import { State } from 'interfaces/state'
import PropertyForm, {
  PropertyFormStateProps,
  PropertyFormDispatchProps,
  PropertyFormValues,
  YesNoValue,
  PropertyFormOwnProps,
} from 'components/features/pfm/PropertyForm/PropertyForm'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { memberInfoSelector } from 'selectors/globalSelectors'
import { homesArraySelector, carsArraySelector } from 'selectors/propertySelectors'
import { homeToFormValues, carToFormValues } from 'helpers/propertyHelpers'
import { getCarMakesActionCreator, getCarModelsActionCreator } from 'actions/propertyActions'
import { HousingType } from 'interfaces/property'
import { createForgottenGuardError } from 'helpers/errorHelpers'

const valueSelector = formValueSelector<State>(FORM_ONBOARDING_PROPERTY)

const initialValuesSelector = createSelector(
  memberInfoSelector,
  homesArraySelector,
  carsArraySelector,
  (member, homes, cars): PropertyFormValues => {
    if (!member) {
      throw createForgottenGuardError('PropertyFormContainer', 'memberGuard')
    }

    const birthDate = member.birth_date
      ? moment(member.birth_date).format(DATE_FORMAT_US)
      : ''

    const income = member.income
      ? Number(member.income)
      : null

    return {
      birth_date: birthDate,
      income,
      housing_type: member.housing_type || null,
      zip5: member.zip5,
      car_owner: cars.length > 0 ? 'yes' : 'no',
      homes: homes.map(homeToFormValues),
      cars: cars.map(carToFormValues),
    }
  },
)

const mapStateToProps = (state: State): PropertyFormStateProps => {
  const member = memberInfoSelector(state)

  if (!member) {
    throw createForgottenGuardError('PropertyFormContainer', 'memberGuard')
  }

  return {
    housing_type: valueSelector(state, 'housing_type') as HousingType,
    car_owner: valueSelector(state, 'car_owner') as YesNoValue,
    alreadySubmitted: member.onboarding_step !== 'property',
    initialValues: initialValuesSelector(state),
    preloadedCars: carsArraySelector(state),
  }
}

const mapDispatchToProps: PropertyFormDispatchProps = {
  getCarMakes: getCarMakesActionCreator,
  getCarModels: getCarModelsActionCreator,

  onCarAdded: (idx) => focus(FORM_ONBOARDING_PROPERTY, `cars[${ idx }].make`),
  onHomeAdded: (idx) => focus(FORM_ONBOARDING_PROPERTY, `homes[${ idx }].address1`),

  change,
}

const propertyFormGuards: Guards = [
  createParallelGuard(
    homesGuard,
    carsGuard,
  ),
]

const PropertyFormContainer = connectLoadable<PropertyFormStateProps, PropertyFormDispatchProps, PropertyFormOwnProps>(
  propertyFormGuards, LoadingOverlay
)(mapStateToProps, mapDispatchToProps)(PropertyForm)
export default PropertyFormContainer
